/**
 * Wealth — Liam's net worth in one place (private; only shown when /api/wealth/access says so).
 * Rules agreed 2026-09-26: no debt, so net worth = what he owns; the companies are 100% his and swept as a dividend
 * every year, so company cash counts as his; joint items count in full. Server is the source of truth: whole-list
 * saves 3s after the last edit, settings patched separately, one snapshot per month. Ported from CEO Tasks.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Lock, Settings2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import { Empty, ErrorNote, PageHeader } from "../components/ui";
import { CATEGORIES, errText, todayISO, useIsPhone, useWealthAccess, WK, type Asset, type Inflows, type WealthDoc } from "../components/wealth/shared";
import { buildMoney } from "../components/wealth/money";
import { OverviewTab } from "../components/wealth/Overview";
import { AssetsTab } from "../components/wealth/Assets";
import { CompaniesTab } from "../components/wealth/Companies";
import { MoneyInTab } from "../components/wealth/MoneyIn";
import { StrategyTab } from "../components/wealth/Strategy";
import { HistoryTab } from "../components/wealth/History";
import { CfoTab } from "../components/wealth/Cfo";
import { WealthSettingsModal } from "../components/wealth/Settings";

const TABS = [["overview", "Overview"], ["assets", "Assets"], ["companies", "Companies"], ["incoming", "Money in"], ["strategy", "Strategy"], ["cfo", "CFO"], ["history", "History"]] as const;
type Tab = (typeof TABS)[number][0];

export function WealthPage() {
  const access = useWealthAccess();
  if (access.isLoading) return <Empty>Loading…</Empty>;
  if (!access.data?.enabled) return <Empty icon={<Lock size={18} />}>Wealth is not available on this account.</Empty>;
  return <Wealth />;
}

function Wealth() {
  const qc = useQueryClient();
  const isPhone = useIsPhone();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const initialTab = (TABS.find(([k]) => k === params.get("tab"))?.[0] ?? "overview") as Tab;
  const [tab, setTabState] = useState<Tab>(initialTab);
  // The Today card opens Strategy straight on the review (?review=1); the flag is read once, then dropped from the URL
  const [openReview] = useState(() => params.get("review") === "1");
  const setTab = (t: Tab) => {
    setTabState(t);
    void navigate({ to: "/wealth", search: { tab: t } as never, replace: true });
  };
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Long staleTime: a refetch must never overwrite asset edits still waiting to save
  const docQ = useQuery({ queryKey: WK.doc, queryFn: () => api<WealthDoc>("/api/wealth"), staleTime: Infinity, refetchOnWindowFocus: false });
  const inflowsQ = useQuery({ queryKey: WK.inflows, queryFn: () => api<Inflows>("/api/wealth/inflows"), staleTime: 5 * 60_000, retry: false, refetchOnWindowFocus: false });
  const doc = docQ.data;

  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [saveState, setSaveState] = useState(""); // '' | 'saving' | 'saved' | 'error: …'
  const dirty = useRef(false);
  const latest = useRef<Asset[] | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    if (doc && !dirty.current) setAssets(doc.assets);
  }, [doc]);
  useEffect(() => () => clearTimeout(timer.current), []);

  // Debounced whole-list save — the server keeps the shrink guard (409); assets only, the settings are never sent from here
  const save = async (next: Asset[], force = false): Promise<void> => {
    setSaveState("saving");
    try {
      const r = await api<{ ok: boolean; updatedAt: string }>("/api/wealth", { method: "PUT", json: { assets: next }, headers: force ? { "X-Force-Sync": "1" } : undefined });
      if (latest.current === next) dirty.current = false; // a newer edit is still waiting otherwise
      setSaveState("saved");
      qc.setQueryData<WealthDoc>(WK.doc, (d) => (d ? { ...d, assets: next, updatedAt: r.updatedAt } : d));
      void qc.invalidateQueries({ queryKey: WK.strategy });
      void qc.invalidateQueries({ queryKey: WK.reviewStatus });
      setTimeout(() => setSaveState((x) => (x === "saved" ? "" : x)), 1500);
    } catch (e) {
      if (!force && e instanceof ApiError && e.status === 409 && window.confirm(`${errText(e)}. The server holds more items than this page is sending. Save this list anyway?`)) return save(next, true);
      setSaveState(`error: ${errText(e)}`);
    }
  };
  const update = (fn: (a: Asset[]) => Asset[]) =>
    setAssets((cur) => {
      if (!cur) return cur;
      const next = fn(cur);
      dirty.current = true;
      latest.current = next;
      clearTimeout(timer.current);
      timer.current = setTimeout(() => void save(next), 3000);
      return next;
    });

  const list = assets ?? [];
  const totals = useMemo(() => {
    const by: Record<string, number> = {};
    for (const c of CATEGORIES) by[c.k] = 0;
    for (const a of list) by[a.category] = (by[a.category] || 0) + (Number(a.value) || 0);
    const total = Object.values(by).reduce((x, y) => x + y, 0);
    return { total, by, liquid: (by.cash ?? 0) + (by.investment ?? 0) };
  }, [list]);
  const money = useMemo(() => buildMoney(inflowsQ.data, doc?.settings?.dividendTaxPct), [inflowsQ.data, doc?.settings?.dividendTaxPct]);

  if (docQ.error) return <div className="space-y-4"><PageHeader title="Wealth" /><ErrorNote error={new Error(errText(docQ.error))} /></div>;
  if (!doc || !assets) return <Empty><Loader2 size={14} className="mr-1 inline animate-spin" /> Loading…</Empty>;

  const settings = doc.settings;
  const companies = settings?.companies || [];
  const asOf = doc.updatedAt ? new Date(doc.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : todayISO();
  const addAsset = (category = "cash") => {
    const a: Asset = { id: crypto.randomUUID(), name: "", category, location: "", value: 0, currency: "EUR", valuedOn: todayISO(), method: "own estimate", owner: "liam", notes: "", createdAt: new Date().toISOString() };
    update((xs) => [a, ...xs]);
    setTab("assets");
  };

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Private"
        title="Wealth"
        subtitle={
          <>
            Values as at {asOf} · EUR · no debt
            {saveState && <span className={saveState.startsWith("error") ? "ml-2 text-red-600" : "ml-2"}>{saveState === "saving" ? "· Saving…" : saveState === "saved" ? "· Saved" : `· ${saveState}`}</span>}
          </>
        }
        actions={<button className="btn btn-sm" onClick={() => setSettingsOpen(true)}><Settings2 size={15} /> Wealth settings</button>}
      />
      <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <div className="tabs" role="tablist">
          {TABS.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tab === k} className="tab" onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>
      </div>

      {tab === "overview" && <OverviewTab assets={assets} totals={totals} settings={settings} money={money} inflowsError={!!inflowsQ.error} />}
      {tab === "assets" && <AssetsTab assets={assets} companies={companies} update={update} addAsset={addAsset} />}
      {tab === "companies" && <CompaniesTab assets={assets} companies={companies} money={money} inflowsLoaded={!!inflowsQ.data} />}
      {tab === "incoming" && <MoneyInTab money={money} inflows={inflowsQ.data} loading={inflowsQ.isLoading} failed={!!inflowsQ.error} isPhone={isPhone} />}
      {tab === "strategy" && <StrategyTab isPhone={isPhone} openReview={openReview} />}
      {tab === "cfo" && <CfoTab isPhone={isPhone} />}
      {tab === "history" && <HistoryTab snapshots={doc.snapshots} isPhone={isPhone} />}

      {settingsOpen && <WealthSettingsModal settings={settings} onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}
